import React, { ReactNode } from 'react'
import { ColorValue, TouchableOpacity, View, ViewProps } from 'react-native'

import CheckIcon from '@common/assets/svg/CheckIcon'
import Text, { Props as TextProps } from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import flexboxStyles from '@common/styles/utils/flexbox'

import styles from './styles'

interface Props {
  label?: ReactNode
  labelProps?: TextProps
  onValueChange: (value: boolean) => void
  value: boolean
  children?: any
  style?: ViewProps['style']
  uncheckedBorderColor?: ColorValue
  checkedColor?: ColorValue
  isDisabled?: boolean
  testID?: string
}

const Checkbox = ({
  label,
  labelProps,
  children,
  onValueChange,
  value,
  style,
  uncheckedBorderColor,
  checkedColor,
  isDisabled,
  testID = 'checkbox'
}: Props) => {
  const { theme } = useTheme()
  const onChange = () => {
    !!onValueChange && onValueChange(!value)
  }

  return (
    <View style={[styles.container, style, isDisabled && { opacity: 0.6 }]}>
      <View style={styles.checkboxWrapper}>
        <TouchableOpacity
          style={[
            styles.webCheckbox,
            {
              borderColor: value
                ? checkedColor || theme.successDecorative
                : uncheckedBorderColor || theme.primaryBorder
            },
            !!value && { backgroundColor: checkedColor || theme.successDecorative }
          ]}
          testID={testID}
          onPress={onChange}
          activeOpacity={0.6}
          disabled={isDisabled}
          accessibilityRole="checkbox"
          // The visible label sits in a sibling <Text>, so the box itself has no
          // text content of its own to derive a name from.
          accessibilityLabel={typeof label === 'string' ? label : undefined}
          // `accessibilityState` is a no-op on react-native-web 0.21 and is kept
          // only for the native build; web needs the aria attribute itself.
          accessibilityState={{ checked: value, disabled: !!isDisabled }}
          // @ts-ignore-next-line — react-native-web only
          aria-checked={value}
          // @ts-ignore-next-line — react-native-web only
          aria-disabled={!!isDisabled}
        >
          {!!value && (
            <CheckIcon color={checkedColor || theme.successDecorative} checkColor={theme.white} />
          )}
        </TouchableOpacity>
      </View>
      <View style={flexboxStyles.flex1}>
        {label ? (
          <Text
            shouldScale={false}
            onPress={onChange}
            appearance="secondaryText"
            fontSize={12}
            {...labelProps}
          >
            {label}
          </Text>
        ) : (
          children
        )}
      </View>
    </View>
  )
}

export default React.memo(Checkbox)
